"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTrialTypeFromUserPrivateMetadata = getTrialTypeFromUserPrivateMetadata;
exports.getTrialInfoFromUserPrivateMetadata = getTrialInfoFromUserPrivateMetadata;
exports.getSyntheticSubscriptionStatusOfTrialSubscription = getSyntheticSubscriptionStatusOfTrialSubscription;
exports.getStateOfTrialSubscription = getStateOfTrialSubscription;
exports.isTrialBlockingPaidCredits = isTrialBlockingPaidCredits;
exports.isEligibleForTrialReactivation = isEligibleForTrialReactivation;
const models_1 = require("@buildwithflux/models");
const trial_1 = require("../config/trial");
const models_2 = require("../models");
const context_1 = require("./context");
const planEpoch_1 = require("./planEpoch");
const subscription_1 = require("./subscription");
/**
 * Parses a watershed date from the config, handling YYYY-MM-DD format
 * Returns the timestamp in milliseconds, or the default watershed if parsing fails
 */
function parseWatershedDate(dateString) {
    // Append time to ensure UTC parsing (YYYY-MM-DD alone can be parsed as local time)
    const parsed = new Date(`${dateString}T00:00:00Z`);
    if (isNaN(parsed.getTime())) {
        return trial_1.TrialReactivationWatershed.valueOf();
    }
    return parsed.getTime();
}
/**
 * @todo Can be simplified after backfill in FLUX-4579 is complete
 *
 * @returns undefined only when the UPM is not loaded or available (e.g. anonymous user)
 */
function getTrialTypeFromUserPrivateMetadata(metadata) {
    if (!metadata) {
        return undefined;
    }
    const planEpoch = (0, planEpoch_1.getPlanEpochFromUserPrivateMetadata)(metadata);
    // Only the new plan epoch supports any trial types
    if (planEpoch !== models_1.PlanEpoch.enum.from_202410) {
        return models_1.TrialType.enum.none;
    }
    return metadata.creationFlags?.trialType ?? models_1.TrialType.enum.reverse;
}
/**
 * Finds either of the two types of trial info depending on the user's trial type
 *
 * @returns undefined if the user has never had a trial, or if the user private metadata cannot be loaded (e.g. anonymous
 *          user), or is not yet loaded (e.g. app startup)
 */
function getTrialInfoFromUserPrivateMetadata(metadata, stripeClientMode = models_1.StripeClientMode.enum.live) {
    if (!metadata) {
        return undefined;
    }
    const trialType = getTrialTypeFromUserPrivateMetadata(metadata);
    // The undefined branch here cannot happen as we've checked metadata is not undefined above
    // The none branch here usually means the user is on a legacy account that does not support trials
    if (trialType === undefined || trialType === models_1.TrialType.enum.none) {
        return undefined;
    }
    const possibleTrials = [
        getGeneratedTrialInfoFromUserPrivateMetadata(metadata, stripeClientMode),
    ];
    if (trialType === models_1.TrialType.enum.payment_details_up_front) {
        possibleTrials.push(getPaymentDetailsUpFrontTrialInfoFromUserPrivateMetadata(metadata, stripeClientMode));
    }
    const trials = possibleTrials
        .filter((trial) => trial !== undefined)
        .sort((t1, t2) => {
        // First sort by trialing status, so that trialing subscriptions are preferred over cancelled ones
        if (t1.status === "trialing" && t2.status !== "trialing") {
            return -1;
        }
        else if (t2.status === "trialing" && t1.status !== "trialing") {
            return 1;
        }
        // Next sort by whether the trial ends at date is in the future
        if (t1.trialEndsAt < Date.now() && t2.trialEndsAt >= Date.now()) {
            return -1;
        }
        else if (t2.trialEndsAt < Date.now() && t1.trialEndsAt >= Date.now()) {
            return 1;
        }
        // Finally, sort by trial end date
        return t1.trialEndsAt - t2.trialEndsAt;
    });
    return trials.pop();
}
/**
 * Finds the user's trial subscription in the user's private metadata, for a trialType=payment_details_up_front, if it exists
 *
 * The subscription may be for a pro or starter account; but needs to still be within the trial period.
 *
 * The returned subscription may be marked as canceled, because the paid plan is no longer in the trial period. Also,
 * the returned subscription may be marked as trialing, even though in Stripe it's actually cancelled, because for
 * business logic reasons we want a user who cancels the payment-details-attached real-product subscription to still
 * receive their two-week trial.
 *
 * Only trialType=payment_details_up_front accounts support this type of trial
 */
function getPaymentDetailsUpFrontTrialInfoFromUserPrivateMetadata(metadata, stripeClientMode = models_1.StripeClientMode.enum.live) {
    if (!metadata) {
        return undefined;
    }
    const productUids = stripeClientMode === models_1.StripeClientMode.enum.live
        ? [models_1.PaymentPlanUids.userPro, models_1.PaymentPlanUids.userStarter, models_1.PaymentPlanUids.userCore]
        : [
            models_1.PaymentPlanUidsForTestMode.userPro,
            models_1.PaymentPlanUidsForTestMode.userStarter,
            models_1.PaymentPlanUidsForTestMode.userCore,
        ];
    const subscription = (0, subscription_1.getSubscriptionForProductFromSubscriptions)(metadata.payment?.subscriptions ?? {}, productUids, false);
    if (!subscription?.trialEndsAt) {
        return undefined;
    }
    // If there's any subscription at all, even if it has been canceled or is past_due, we still want the trial
    // to be able to run. The only disqualifying feature is if the trialEndsAt is in the past, and that's checked by
    // the callers of this method already.
    return {
        trialEndsAt: subscription.trialEndsAt,
        status: models_1.SubscriptionStatus.enum.trialing,
        subscription,
    };
}
/**
 * Finds the user's trial subscription in the generated trials section of a user's private metadata, if it exists
 *
 * The returned subscription may be canceled, in which case the subscription is treated the same as if it expired
 *
 * The subscription is matched by the product the subscription contains (it matches StripeNewAccountTrialProduct): a
 * free $0/mo product that results in no charges and does not convert to a paid plan automatically when the trial ends.
 *
 * Note that both trialType=reverse and trialType=payment_details_up_front accounts support this type of trial, because
 * when we reactivate a trial on a dormant account, we use this type of trial (to prevent unexpected charges - the user
 * may have given us their CC details several months or years ago, and we don't want to suddenly resurrect billing against
 * those details unless they agree to it by going through a checkout process.)
 */
function getGeneratedTrialInfoFromUserPrivateMetadata(metadata, stripeClientMode = models_1.StripeClientMode.enum.live) {
    if (!metadata) {
        return undefined;
    }
    const subscription = (0, subscription_1.getSubscriptionForProductFromSubscriptions)(metadata.payment?.generatedTrialSubscriptions ?? {}, stripeClientMode === models_1.StripeClientMode.enum.live
        ? models_1.StripeNewAccountTrialProduct
        : models_1.StripeNewAccountTrialProductForTestMode, false);
    if (!subscription?.trialEndsAt) {
        return undefined;
    }
    return {
        trialEndsAt: subscription.trialEndsAt,
        status: subscription.status === models_1.SubscriptionStatus.enum.trialing ||
            subscription.status === models_1.SubscriptionStatus.enum.active
            ? models_1.SubscriptionStatus.enum.trialing
            : models_1.SubscriptionStatus.enum.canceled,
        subscription,
    };
}
/**
 * Gets the effective subscription status (generally trialing or canceled) of a trial subscription, considering the
 * current time versus when the subscription's trial ends
 *
 * This subscription status is generally used to determine whether a trial subscription's matching plan should be
 * eligible to contribute entitlements to the merge-down of effective entitlements
 */
function getSyntheticSubscriptionStatusOfTrialSubscription(subscription) {
    const status = subscription?.status ?? models_1.SubscriptionStatus.enum.canceled;
    if (!subscription || status !== models_1.SubscriptionStatus.enum.trialing) {
        return status;
    }
    // Even if the subscription is active or trialing, if the trial ends at date has passed, it's considered canceled
    if (subscription.trialEndsAt && subscription.trialEndsAt < Date.now()) {
        return models_1.SubscriptionStatus.enum.canceled;
    }
    return status;
}
/**
 * Gets an IndividualTrialState for a trial subscription
 *
 * This state is used in the UI to distinguish trials that have expired from ones that are expiring or active. This
 * function further distinguishes between trials that are expiring soon (2 days or less) and those that are still active,
 * and users that have never had a trial
 */
function getStateOfTrialSubscription(subscription) {
    if (!subscription) {
        return models_2.IndividualTrialState.enum.none;
    }
    if (subscription.status !== models_1.SubscriptionStatus.enum.trialing) {
        return models_2.IndividualTrialState.enum.expired;
    }
    const daysRemainingInTrial = subscription.trialEndsAt
        ? (subscription.trialEndsAt - Date.now()) / (1000 * 60 * 60 * 24)
        : 0;
    if (daysRemainingInTrial < 0) {
        return models_2.IndividualTrialState.enum.expired;
    }
    return daysRemainingInTrial > 2 ? models_2.IndividualTrialState.enum.active : models_2.IndividualTrialState.enum.expiring;
}
function isTrialBlockingPaidCredits(metadata, stripeClientMode = models_1.StripeClientMode.enum.live) {
    if (!metadata?.creationFlags?.endTrialOnFreeCreditsExhausted) {
        return false;
    }
    const trialType = getTrialTypeFromUserPrivateMetadata(metadata);
    if (trialType !== models_1.TrialType.enum.payment_details_up_front) {
        return false;
    }
    const trialInfo = getTrialInfoFromUserPrivateMetadata(metadata, stripeClientMode);
    const trialState = getStateOfTrialSubscription(trialInfo);
    return trialState === models_2.IndividualTrialState.enum.active || trialState === models_2.IndividualTrialState.enum.expiring;
}
/**
 * Checks if a user is eligible for trial reactivation
 *
 * @param user - The user data
 * @param userPrivateMetadata - The user's private metadata
 * @param planEntitlement - The plan entitlement service
 * @param stripeClientMode - The Stripe client mode (live or test)
 * @param configOverride - Optional feature flag config to override default constants.
 *                         When undefined/null, uses code constants.
 *                         When { enabled: false }, reactivation is disabled.
 *                         When { enabled: true, watershedDate, minimumExpiredDays }, uses those values.
 */
function isEligibleForTrialReactivation(user, userPrivateMetadata, planEntitlement, stripeClientMode, configOverride) {
    // Parse and validate the config, falling back to defaults if invalid
    const config = (0, models_1.getTrialReactivationConfig)(configOverride);
    // Check if reactivation is explicitly disabled via feature flag
    if ("enabled" in config && config.enabled === false) {
        return {
            isEligible: false,
            reason: "Trial reactivation is currently disabled",
        };
    }
    // Determine watershed and minimum expired days from config or constants
    let watershedMs;
    let minimumExpiredDays;
    if ("enabled" in config && config.enabled === true) {
        // Explicit config with required values
        watershedMs = parseWatershedDate(config.watershedDate);
        minimumExpiredDays = config.minimumExpiredDays;
    }
    else {
        // Default config (empty object) - use code constants
        watershedMs = trial_1.TrialReactivationWatershed.valueOf();
        minimumExpiredDays = trial_1.TrialReactivationMinimumExpiredDays;
    }
    const planContext = (0, context_1.getUserContextFromParts)(user, userPrivateMetadata, stripeClientMode);
    const category = planEntitlement.getEntitlements(planContext)["category"];
    if (category.value === models_1.PaymentPlanCategory.enum.userEdu ||
        category.value === models_1.PaymentPlanCategory.enum.userLegacyEdu) {
        return {
            isEligible: false,
            reason: "Educational users do not need to reactivate their trial",
        };
    }
    if ((0, models_1.isPaidPaymentPlanCategory)(category.value)) {
        return {
            isEligible: false,
            reason: "User should have no paid plan to reactivate their trial",
        };
    }
    const planEpoch = (0, planEpoch_1.getPlanEpochFromUserPrivateMetadata)(userPrivateMetadata);
    if (planEpoch === models_1.PlanEpoch.enum.legacy) {
        return {
            isEligible: false,
            reason: "User must not be in legacy plan epoch to have a trial",
        };
    }
    const customerUid = userPrivateMetadata.payment?.customerUid;
    if (!customerUid) {
        return {
            isEligible: false,
            reason: "User should have an existing customer ID to reactivate their trial",
        };
    }
    const trialInfo = getTrialInfoFromUserPrivateMetadata(userPrivateMetadata, stripeClientMode);
    if (!trialInfo) {
        return {
            isEligible: false,
            reason: "User must have had an existing trial to reactivate",
        };
    }
    const trialState = getStateOfTrialSubscription(trialInfo);
    if (trialState === "active") {
        return {
            isEligible: false,
            reason: "User already has an active trial",
        };
    }
    if (trialInfo.trialEndsAt > watershedMs) {
        return {
            isEligible: false,
            reason: `Previous trial ended after watershed (${new Date(trialInfo.trialEndsAt).toISOString()} > ${new Date(watershedMs).toISOString()})`,
        };
    }
    const minimumExpiredMs = minimumExpiredDays * 24 * 60 * 60 * 1000;
    const cutoffTime = Date.now() - minimumExpiredMs;
    if (trialInfo.trialEndsAt > cutoffTime) {
        return {
            isEligible: false,
            reason: `Trial ended too recently (${new Date(trialInfo.trialEndsAt).toISOString()}, must have been expired for at least ${minimumExpiredDays} days)`,
        };
    }
    return {
        isEligible: true,
        customerUid,
    };
}
//# sourceMappingURL=trial.js.map